import { createFileRoute } from '@tanstack/react-router';
import { PageBody, Tab, TabList } from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { useHashTabs } from '@/lib/useHashTabs.ts';
import { HistoryTab } from '@/components/admin/broadcast/HistoryTab.tsx';
import { SendTab } from '@/components/admin/broadcast/SendTab.tsx';
import { TemplatesTab } from '@/components/admin/broadcast/TemplatesTab.tsx';
import { useBroadcastTemplates } from '@/components/admin/broadcast/useBroadcastTemplates.ts';

/*
 * A link may name the template to send — the same `?template=` the Vue console
 * read, so the links staff already have keep working.
 */
export interface BroadcastSearch {
  template?: string;
}

function validateSearch(search: Record<string, unknown>): BroadcastSearch {
  return { template: typeof search.template === 'string' ? search.template : undefined };
}

export const Route = createFileRoute('/_app/admin/broadcast')({
  staticData: { orgScope: 'required' },
  validateSearch,
  component: BroadcastRoute,
});

const TABS = [
  { key: 'send', label: 'Send' },
  { key: 'templates', label: 'Templates' },
  { key: 'history', label: 'History' },
];

function BroadcastRoute() {
  const { template } = Route.useSearch();

  return <BroadcastPage preselected={template} />;
}

export interface BroadcastPageProps {
  preselected?: string;
}

/**
 * The one page for broadcasts: sending one, keeping the templates they are sent
 * from, and what came of the ones already sent.
 *
 * Send and Templates are two ends of the same list, which is why they share a
 * page and a query rather than sitting under two rail entries.
 */
export function BroadcastPage({ preselected }: BroadcastPageProps) {
  const { active, select } = useHashTabs(TABS);
  const templates = useBroadcastTemplates(active === 'send' || active === 'templates');

  return (
    <>
      <PageHeader title='Broadcast' />
      <PageBody className='mt-3.5'>
        <div className='flex flex-col gap-5'>
          <p className='text-sm text-ink-muted'>
            Send messages, manage templates, and view broadcast history.
          </p>

          <TabList aria-label='Broadcast sections'>
            {TABS.map((tab) => (
              <Tab
                key={tab.key}
                active={tab.key === active}
                onClick={() => select(tab.key)}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          {active === 'send' && (
            <SendTab templates={templates} preselected={preselected} />
          )}
          {active === 'templates' && <TemplatesTab templates={templates} />}
          {active === 'history' && <HistoryTab />}
        </div>
      </PageBody>
    </>
  );
}
